<template>
    <section
        v-if="hasItems"
        ref="sectionRef"
        class="prompt-recommendations"
        :class="{
            'prompt-recommendations--flush': props.flushTopGap,
            'prompt-recommendations--integrated': props.integrated,
            'prompt-recommendations--after-toolbar': props.afterToolbar,
            'prompt-recommendations--collapsed': isCollapsed,
        }"
    >
        <div class="prompt-recommendations__header">
            <div class="prompt-recommendations__title-row">
                <h2>{{ t('promptRecommendations.title') }}</h2>
                <button
                    v-if="!isCollapsed"
                    type="button"
                    class="prompt-recommendations__refresh"
                    :disabled="isRefreshing"
                    :aria-label="t('promptRecommendations.refresh')"
                    @click="refresh"
                >
                    <svg
                        aria-hidden="true"
                        :class="{ spinning: isRefreshing }"
                        viewBox="0 0 20 20"
                        fill="none"
                    >
                        <path
                            d="M15.82 7.02A6.5 6.5 0 1 0 16.5 10"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                        />
                        <path
                            d="M12.8 6.95h3.2v-3.2"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>
            <button
                type="button"
                class="prompt-recommendations__hide"
                :aria-expanded="!isCollapsed"
                @click="toggleCollapsed"
            >
                <span>
                    {{
                        t(
                            isCollapsed
                                ? 'promptRecommendations.show'
                                : 'promptRecommendations.hide'
                        )
                    }}
                </span>
                <svg
                    aria-hidden="true"
                    :class="{ 'is-collapsed': isCollapsed }"
                    viewBox="0 0 12 12"
                    fill="none"
                >
                    <path
                        d="m2.5 4.5 3.5 3 3.5-3"
                        stroke="currentColor"
                        stroke-width="1.4"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </button>
        </div>

        <div v-if="!isCollapsed" class="prompt-recommendations__list">
            <button
                v-for="item in visibleItems"
                :key="item.id"
                type="button"
                class="prompt-recommendations__card"
                @click="selectItem(item)"
            >
                <span v-if="!isTextPromptMode" class="prompt-recommendations__media">
                    <img
                        :src="item.imageUrl"
                        alt=""
                        loading="lazy"
                        @click.stop
                    />
                </span>
                <span class="prompt-recommendations__prompt">{{
                    item.prompt
                }}</span>
            </button>
        </div>
    </section>

    <Teleport to="body">
        <div
            v-if="pendingItem"
            class="prompt-recommendations__modal-mask"
            role="presentation"
            @click.self="cancelOverwrite"
        >
            <div
                class="prompt-recommendations__modal"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="overwriteTitleId"
                :aria-describedby="overwriteDescriptionId"
            >
                <h3 :id="overwriteTitleId">
                    {{ t('promptRecommendations.overwriteTitle') }}
                </h3>
                <p :id="overwriteDescriptionId">
                    {{ t('promptRecommendations.overwriteDescription') }}
                </p>
                <div class="prompt-recommendations__modal-actions">
                    <button type="button" @click="cancelOverwrite">
                        {{ t('common.cancel') }}
                    </button>
                    <button
                        type="button"
                        class="prompt-recommendations__modal-confirm"
                        @click="confirmOverwrite"
                    >
                        {{ t('common.confirm') }}
                    </button>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    useId,
    watch,
} from 'vue'
import { useRoute } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useUserStore } from '~/stores/user'
import { analyticsEvent } from '~/utils/log-event-func'
import {
    isPromptRecommendationModeSupported,
    modelPromptRecommendations,
    normalizePromptRecommendationMode,
    type ModelPromptRecommendation,
    type PromptRecommendationMedia,
} from '~/utils/modelPromptRecommendations'

interface Props {
    media: PromptRecommendationMedia
    landingModelKey?: string
    modelKey?: string
    mode?: string
    hasContent?: boolean
    flushTopGap?: boolean
    integrated?: boolean
    afterToolbar?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    landingModelKey: '',
    modelKey: '',
    mode: '',
    hasContent: false,
    flushTopGap: false,
    integrated: false,
    afterToolbar: false,
})

const emit = defineEmits<{
    select: [item: ModelPromptRecommendation]
}>()

const route = useRoute()
const { t, locale } = useI18n()
const userStore = useUserStore()
const sectionRef = ref<HTMLElement | null>(null)
const page = ref(0)
const recommendationOrder = ref<string[]>([])
const isRefreshing = ref(false)
const isCollapsed = ref(false)
const isIntersecting = ref(false)
const pendingItem = ref<ModelPromptRecommendation | null>(null)
const lastExposureKey = ref('')
const componentId = useId()
const overwriteTitleId = `prompt-overwrite-title-${componentId}`
const overwriteDescriptionId = `prompt-overwrite-description-${componentId}`
let observer: IntersectionObserver | null = null
let refreshTimer: number | null = null

const normalizedMode = computed(() =>
    normalizePromptRecommendationMode(props.mode || props.media)
)
const exposureScope = computed(
    () =>
        `${props.modelKey || props.landingModelKey || 'unknown'}:${normalizedMode.value}`
)
const modeSupported = computed(() =>
    isPromptRecommendationModeSupported(props.media, props.mode)
)
const isTextPromptMode = computed(() =>
    normalizedMode.value === 'text_to_image' ||
    normalizedMode.value === 'text_to_video'
)
const items = computed(() =>
    modeSupported.value
        ? modelPromptRecommendations[props.media].map(item => ({
              id: item.id,
              imageUrl: item.imageUrl,
              prompt: t(item.promptKey),
          }))
        : []
)
const hasItems = computed(() => items.value.length > 0)

const randomizeRecommendations = (previousIds: string[] = []) => {
    const shuffled = items.value.map(item => item.id)
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1))
        const currentId = shuffled[index]
        const randomId = shuffled[randomIndex]
        if (currentId === undefined || randomId === undefined) continue
        shuffled[index] = randomId
        shuffled[randomIndex] = currentId
    }

    const visibleCount = Math.min(3, shuffled.length)
    const previousIdSet = new Set(previousIds)
    const firstNewIndex = shuffled.findIndex(
        (id, index) => index >= visibleCount && !previousIdSet.has(id)
    )
    if (
        previousIds.length > 0 &&
        shuffled.slice(0, visibleCount).every(id => previousIdSet.has(id)) &&
        firstNewIndex >= visibleCount
    ) {
        const firstId = shuffled[0]
        const newId = shuffled[firstNewIndex]
        if (firstId !== undefined && newId !== undefined) {
            shuffled[0] = newId
            shuffled[firstNewIndex] = firstId
        }
    }
    recommendationOrder.value = shuffled
}

const visibleItems = computed(() => {
    const count = Math.min(3, items.value.length)
    const orderedIds = recommendationOrder.value.length
        ? recommendationOrder.value
        : items.value.map(item => item.id)
    const itemsById = new Map(items.value.map(item => [item.id, item]))
    return orderedIds
        .slice(0, count)
        .map(id => itemsById.get(id))
        .filter((item): item is ModelPromptRecommendation => Boolean(item))
})

const track = (event: string, item?: ModelPromptRecommendation) => {
    analyticsEvent(event, {
        page_path: route.path,
        landing_model: props.landingModelKey || props.modelKey,
        current_model: props.modelKey,
        type: props.media,
        creation_mode: normalizedMode.value,
        case_id: item?.id,
        language: locale.value,
        login_status: userStore.isLoggedIn ? 'logged_in' : 'logged_out',
    })
}

const trackExposure = () => {
    if (!isIntersecting.value || !hasItems.value || isCollapsed.value) return
    const exposureKey = `${exposureScope.value}:${page.value}`
    if (lastExposureKey.value === exposureKey) return
    lastExposureKey.value = exposureKey
    track('view_model_prompt_recommendation')
    visibleItems.value.forEach(item =>
        track('view_model_prompt_recommendation_case', item)
    )
}

const observeSection = () => {
    observer?.disconnect()
    observer = null
    if (!import.meta.client || !sectionRef.value) return
    observer = new IntersectionObserver(
        entries => {
            isIntersecting.value = Boolean(entries[0]?.isIntersecting)
            trackExposure()
        },
        { threshold: 0.2 }
    )
    observer.observe(sectionRef.value)
}

const refresh = () => {
    if (isRefreshing.value) return
    isRefreshing.value = true
    randomizeRecommendations(visibleItems.value.map(item => item.id))
    page.value += 1
    track('click_model_prompt_recommendation_refresh')
    nextTick(trackExposure)
    refreshTimer = window.setTimeout(() => {
        isRefreshing.value = false
        refreshTimer = null
    }, 220)
}

const toggleCollapsed = () => {
    isCollapsed.value = !isCollapsed.value
    if (isCollapsed.value) {
        track('click_model_prompt_recommendation_hide')
    } else {
        lastExposureKey.value = ''
        nextTick(trackExposure)
        track('click_model_prompt_recommendation_show')
    }
}

const applyItem = (item: ModelPromptRecommendation) => {
    track('click_model_prompt_recommendation', item)
    emit('select', item)
}

const selectItem = (item: ModelPromptRecommendation) => {
    if (props.hasContent) {
        pendingItem.value = item
        return
    }
    applyItem(item)
}

const cancelOverwrite = () => {
    if (pendingItem.value)
        track('cancel_model_prompt_recommendation_overwrite', pendingItem.value)
    pendingItem.value = null
}

const confirmOverwrite = () => {
    const item = pendingItem.value
    if (!item) return
    pendingItem.value = null
    track('confirm_model_prompt_recommendation_overwrite', item)
    applyItem(item)
}

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && pendingItem.value) cancelOverwrite()
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
    randomizeRecommendations()
    nextTick(observeSection)
})

onUnmounted(() => {
    observer?.disconnect()
    if (refreshTimer) window.clearTimeout(refreshTimer)
    document.removeEventListener('keydown', handleKeydown)
})

watch(exposureScope, () => {
    page.value = 0
    randomizeRecommendations()
    lastExposureKey.value = ''
    nextTick(observeSection)
})

watch(isCollapsed, () => {
    lastExposureKey.value = ''
    nextTick(observeSection)
})
</script>

<style scoped lang="less">
.prompt-recommendations {
    box-sizing: border-box;
    width: 100%;
    margin-top: 0;
    padding: 14px 16px 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-top-color: rgba(255, 255, 255, 0.14);
    border-radius: 0 0 18px 18px;
    background: rgba(24, 22, 27, 0.76);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
}
.prompt-recommendations--flush {
    position: relative;
    width: calc(100% + 32px);
    margin: -20px -16px -16px;
    padding: 12px 16px 14px;
    border: 0;
    border-radius: 0 0 19px 19px;
    background: linear-gradient(
        180deg,
        rgba(28, 28, 30, 0) 0,
        rgba(22, 21, 24, 0.42) 18px,
        rgba(18, 17, 20, 0.62) 100%
    );
    box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.04);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
}
.prompt-recommendations--integrated {
    margin-top: -1px;
    border-color: rgba(255, 255, 255, 0.08);
    border-top: 0;
    border-radius: 0 0 24px 24px;
    background: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(25px);
    -webkit-backdrop-filter: blur(25px);
}
.prompt-recommendations--after-toolbar {
    margin-top: 0;
    border-radius: 18px;
    border-color: rgba(255, 255, 255, 0.1);
    background: rgba(24, 22, 27, 0.76);
}
.prompt-recommendations--collapsed {
    padding-bottom: 14px;
}

.prompt-recommendations__header,
.prompt-recommendations__title-row {
    display: flex;
    align-items: center;
}
.prompt-recommendations__header {
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}
.prompt-recommendations--collapsed .prompt-recommendations__header {
    margin-bottom: 0;
}
.prompt-recommendations__header h2 {
    margin: 0;
    color: rgba(255, 255, 255, 0.58);
    font-size: 14px;
    font-weight: 400;
}
.prompt-recommendations__title-row {
    min-width: 0;
    gap: 6px;
}
.prompt-recommendations__refresh,
.prompt-recommendations__hide {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
}
.prompt-recommendations__refresh:disabled {
    cursor: wait;
    opacity: 0.6;
}
.prompt-recommendations__refresh {
    width: 24px;
    height: 24px;
    padding: 2px;
}
.prompt-recommendations__hide {
    flex-shrink: 0;
    gap: 7px;
    padding: 2px 0 2px 8px;
    font-size: 13px;
    line-height: 20px;
}
.prompt-recommendations__refresh svg {
    width: 18px;
    height: 18px;
}
.prompt-recommendations__hide svg {
    width: 12px;
    height: 12px;
}
.prompt-recommendations__hide svg.is-collapsed {
    transform: rotate(180deg);
}
.prompt-recommendations__refresh:hover:not(:disabled),
.prompt-recommendations__hide:hover {
    color: #99f768;
}
.spinning {
    display: inline-block;
    animation: prompt-refresh-spin 0.22s linear;
}

.prompt-recommendations__list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.prompt-recommendations__card {
    display: flex;
    width: 100%;
    min-height: 54px;
    min-width: 0;
    align-items: center;
    gap: 12px;
    padding: 5px 0;
    overflow: visible;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #fff;
    text-align: left;
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;
}
.prompt-recommendations__card:hover {
    background: rgba(255, 255, 255, 0.045);
}
.prompt-recommendations__media {
    position: relative;
    width: 46px;
    height: 46px;
    flex: 0 0 46px;
}
.prompt-recommendations__media img {
    width: 100%;
    height: 100%;
    border-radius: 8px;
    object-fit: cover;
    transform-origin: center;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
}
.prompt-recommendations__media:hover {
    z-index: 2;
}
.prompt-recommendations__media:hover img {
    position: relative;
    z-index: 1;
    transform: scale(1.35);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.38);
}
.prompt-recommendations__prompt {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    color: rgba(255, 255, 255, 0.86);
    font-size: 14px;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.prompt-recommendations__modal-mask {
    position: fixed;
    z-index: 10000;
    inset: 0;
    display: grid;
    place-items: center;
    padding: 20px;
    background: rgba(0, 0, 0, 0.68);
    backdrop-filter: blur(6px);
}
.prompt-recommendations__modal {
    width: min(420px, 100%);
    padding: 24px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 18px;
    background: #1c1c1f;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45);
}
.prompt-recommendations__modal h3 {
    margin: 0;
    color: #fff;
    font-size: 20px;
    font-weight: 600;
}
.prompt-recommendations__modal p {
    margin: 10px 0 22px;
    color: rgba(255, 255, 255, 0.62);
    font-size: 14px;
    line-height: 1.6;
}
.prompt-recommendations__modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}
.prompt-recommendations__modal-actions button {
    min-width: 88px;
    padding: 10px 18px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
    cursor: pointer;
}
.prompt-recommendations__modal-actions .prompt-recommendations__modal-confirm {
    border-color: #99f768;
    background: #99f768;
    color: #10140e;
    font-weight: 600;
}

@keyframes prompt-refresh-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 767px) {
    .prompt-recommendations {
        padding: 12px 14px 14px;
    }
    .prompt-recommendations__media {
        width: 42px;
        height: 42px;
        flex-basis: 42px;
    }
    .prompt-recommendations__card {
        min-height: 50px;
        gap: 10px;
    }
    .prompt-recommendations__prompt {
        font-size: 13px;
    }
}
</style>
